import { useContext, useEffect, useState } from 'react';
import { TestingHqContext } from '../../context/TestingHqContext';
import { TestingContext } from '../../context/TestingContext';
import TESTING_PLACE_SCHEDULE_STATUS from '../../constants/testing_place_schedule';
import {
  get_testing_place_list,
  get_testing_schedule_list,
  get_testing_place_schedule_list,
  get_testing_type,
  get_testing_list,
  get_testing_accept_summary,
  upsert_testing_place,
  upsert_testing_place_schedule,
} from '../../utils/restapi';
import './plan.css';
import ListBody from '../../components/list/ListBody';

// 진행위원 역할 — testing_teacher/setup.js 와 동일한 5개 역할을 그대로 사용한다
// (기존 페이지가 testing_place.data.teachers 에 이미 쓰고 있는 필드명과 호환).
const TEACHER_ROLES = [
  { key: 'master', label: '총감독위원', placeholder: '예: 고사장 전체 총괄, 민원 및 비상 상황 대응' },
  { key: 'main', label: '시험감독위원(정)', placeholder: '예: 고사실 내 시험 통제, 본인 확인, 문답지 배부 및 회수' },
  { key: 'sub', label: '시험감독위원(부)', placeholder: '예: 정감독 보조, 문답지 배부 및 회수 보조' },
  { key: 'info', label: '통제위원', placeholder: '예: 정·후문 출입 통제, 수험생 동선 안내 및 소음 관리, 복도 감독' },
  { key: 'reserve', label: '관리위원', placeholder: '예: 타종 등 방송 시설 제어, 수험표 출력 및 행정 지원, 시험 준비 및 정리' },
];

const REPORT_STATUS_LABEL = {
  draft: '작성 중',
  submitted: '보고 완료',
  approved: '본부 승인',
};

// 4단계 프로세스 — 상단 네비게이션과 섹션 id 매핑에 공통으로 사용
// (기본 정보는 별도 고정 영역이라 프로세스 목록에서 제외)
const PROCESSES = [
  { no: 2, id: 'plan-section-2', label: '고사실 세부 설정' },
  { no: 3, id: 'plan-section-3', label: '진행위원 편성계획' },
  { no: 4, id: 'plan-section-4', label: '시설사용료 계획서' },
  { no: 5, id: 'plan-section-5', label: '특이사항 및 최종 보고' },
];

const emptyRoom = (testingPlaceObj, testingScheduleObj, code) => ({
  testing_place_id: testingPlaceObj.testing_place_id,
  testing_schedule_id: testingScheduleObj.testing_schedule_id,
  code: String(code),
  room_no: `${code}고사실`,
  max_users: 0,
  main_teachers: 0,
  sub_teachers: 0,
  data: { seat: { rows: 0, columns: 0, direction: 'vertical' }, locationName: '' },
  status: TESTING_PLACE_SCHEDULE_STATUS.field.join,
});

const TestingPlaceSchedulePlan = () => {
  const { testingHqs } = useContext(TestingHqContext);
  const { testingObj, setTestingObj } = useContext(TestingContext);

  const [testingTypes, setTestingTypes] = useState([]);
  const [testings, setTestings] = useState([]);
  const [testingSchedules, setTestingSchedules] = useState([]);
  const [testingSchedule, setTestingSchedule] = useState(null);
  const [testingPlaces, setTestingPlaces] = useState([]);
  const [testingPlace, setTestingPlace] = useState(null);
  // 최종 확정 접수 인원 — testing_place.accept_users 요약컬럼이 갱신 안 돼 0 으로 남는 경우가 있어,
  // testing_accept 에서 지역본부(hcodes) 무관하게 라이브 집계한 값으로 덮어쓴다. (list.js 와 동일 전략)
  const [liveAcceptUsers, setLiveAcceptUsers] = useState(null);

  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState({ master: 0, main: 0, sub: 0, info: 0, reserve: 0, duties: {} });
  const [costs, setCosts] = useState([]);
  const [notes, setNotes] = useState('');
  const [reportStatus, setReportStatus] = useState('draft');
  // 2→3→4→5 순차 확정 상태 (확정된 단계는 잠기고 다음 단계가 열린다)
  const [stepConfirmed, setStepConfirmed] = useState({ rooms: false, teachers: false, costs: false });
  // 5단계에서 "제출 전 내용 확인" 화면 표시 여부 (임시 UI 상태, 저장 대상 아님)
  const [reviewMode, setReviewMode] = useState(false);

  const isLocked = reportStatus !== 'draft';

  // ---- 조회 ----
  const getTestingTypes = async () => {
    try {
      setTestingTypes(await get_testing_type());
    } catch (error) {
      console.error('시험유형 조회 실패:', error);
    }
  };

  const getTestings = async (type) => {
    if (!type) { setTestings([]); return; }
    try {
      const response = await get_testing_list({ type, page: 1, rowsPerPage: 1000, sort: 'a_testing_id', desc: 'desc' });
      setTestings(response.lists || response);
    } catch (error) {
      console.error('시험 조회 실패:', error);
      setTestings([]);
    }
  };

  const getTestingSchedules = async (testing_id) => {
    if (!testing_id) { setTestingSchedules([]); return; }
    try {
      const response = await get_testing_schedule_list({ testing_id, page: 1, rowsPerPage: 1000 });
      setTestingSchedules(response.lists || []);
      if (response.lists.length === 1) setTestingSchedule(response.lists[0]);
    } catch (error) {
      console.error('일정 조회 실패:', error);
      setTestingSchedules([]);
    }
  };

  const getTestingPlaces = async () => {
    if (!testingObj?.testing_id || testingHqs.length === 0) { setTestingPlaces([]); return; }
    try {
      const response = await get_testing_place_list({
        testing_id: testingObj.testing_id,
        gcodes: testingHqs.map((hq) => hq.code).join(','),
        page: 1,
        rowsPerPage: 1000,
        sort: 'a_gcode,a_code',
      });
      setTestingPlaces(response.lists || []);
      if (response.lists?.length === 1) setTestingPlace(response.lists[0]);
    } catch (error) {
      console.error('고사장 조회 실패:', error);
      setTestingPlaces([]);
    }
  };

  // testing_place.rooms 는 응시자명단/좌석배치도 등 다른 화면이 참조하는 요약 컬럼이라
  // 실제 고사실 개수와 어긋나 있으면 여기서 바로잡는다.
  const syncTestingPlaceRooms = async (count) => {
    if (!testingPlace?.testing_place_id) return;
    if (parseInt(testingPlace.rooms, 10) === count) return;
    try {
      await upsert_testing_place({ testing_place_id: testingPlace.testing_place_id, rooms: count });
      setTestingPlace((prev) => (prev ? { ...prev, rooms: count } : prev));
    } catch (error) {
      console.error('고사실수 동기화 실패:', error);
    }
  };

  const getRooms = async () => {
    if (!testingPlace?.testing_place_id || !testingSchedule?.testing_schedule_id) { setRooms([]); return; }
    try {
      const response = await get_testing_place_schedule_list({
        testing_place_id: testingPlace.testing_place_id,
        testing_schedule_id: testingSchedule.testing_schedule_id,
        page: 1,
        rowsPerPage: 1000,
        sort: 'a_code*1',
        desc: 'asc',
      });
      const lists = (response.lists || []).filter((row) =>
        TESTING_PLACE_SCHEDULE_STATUS.used.includes(parseInt(row.status, 10))
      );
      setRooms(lists.length > 0 ? lists : [emptyRoom(testingPlace, testingSchedule, 1)]);
      syncTestingPlaceRooms(lists.length);
    } catch (error) {
      console.error('고사실 조회 실패:', error);
      setRooms([]);
    }
  };

  useEffect(() => { getTestingTypes(); }, []);

  useEffect(() => {
    if (testingObj?.type) getTestings(testingObj.type);
    else setTestings([]);
    setTestingPlace(null);
    setTestingPlaces([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingObj?.type]);

  useEffect(() => {
    if (testingObj?.testing_id) {
      getTestingSchedules(testingObj.testing_id);
      getTestingPlaces();
    } else {
      setTestingSchedules([]);
      setTestingPlaces([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingObj?.testing_id, testingHqs.length]);

  const getAcceptSummary = async () => {
    if (!testingObj?.testing_id || !testingPlace?.testing_place_id || !testingSchedule?.testing_schedule_id) {
      setLiveAcceptUsers(null);
      return;
    }
    try {
      const response = await get_testing_accept_summary({
        testing_id: testingObj.testing_id,
        testing_schedule_id: testingSchedule.testing_schedule_id,
      });
      const row = (response.lists || []).find(
        (r) => Number(r.testing_place_id) === Number(testingPlace.testing_place_id)
      );
      setLiveAcceptUsers(row ? parseInt(row.accept_users, 10) || 0 : 0);
    } catch (error) {
      console.error('접수인원 집계 조회 실패:', error);
      setLiveAcceptUsers(null);
    }
  };

  useEffect(() => {
    if (testingPlace?.testing_place_id && testingSchedule?.testing_schedule_id) {
      getRooms();
      getAcceptSummary();
    } else {
      setRooms([]);
      setLiveAcceptUsers(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingPlace?.testing_place_id, testingSchedule?.testing_schedule_id]);

  // testingPlace 가 바뀌면 Section 3~5 상태를 해당 고사장의 data 로 초기화
  useEffect(() => {
    const data = testingPlace?.data || {};
    const counts = {
      master: data.teachers?.master || 0,
      main: data.teachers?.main || 0,
      sub: data.teachers?.sub || 0,
      info: data.teachers?.info || 0,
      reserve: data.teachers?.reserve || 0,
    };
    const rawDuties = data.teachers?.duties || {};
    const duties = {};
    for (const key of Object.keys(counts)) {
      const count = parseInt(counts[key], 10) || 0;
      const prevList = Array.isArray(rawDuties[key]) ? rawDuties[key] : [];
      duties[key] = Array.from({ length: count }, (_, i) => prevList[i] || '');
    }
    setTeachers({ ...counts, duties });
    setCosts(data.costs || []);
    setNotes(data.notes || '');
    setReportStatus(data.reportStatus || 'draft');
    setStepConfirmed({
      rooms: !!data.stepConfirmed?.rooms,
      teachers: !!data.stepConfirmed?.teachers,
      costs: !!data.stepConfirmed?.costs,
    });
    setReviewMode(false);
  }, [testingPlace]);

  // ---- 셀렉터 핸들러 ----
  const handleTestingTypeChange = (e) => setTestingObj({ type: e.target.value });
  const handleTestingChange = (e) => {
    const selected = testings.find((o) => o.testing_id === parseInt(e.target.value));
    setTestingObj(selected || null);
  };
  const handleTestingScheduleChange = (e) => {
    const selected = testingSchedules.find((o) => o.testing_schedule_id === parseInt(e.target.value));
    setTestingSchedule(selected || null);
  };
  const handleTestingPlaceChange = (e) => {
    const selected = testingPlaces.find((o) => o.testing_place_id === parseInt(e.target.value));
    setTestingPlace(selected || null);
  };

  // ---- 상단 프로세스 네비게이션 ----
  const PROCESS_CONFIRM_KEY = { 2: 'rooms', 3: 'teachers', 4: 'costs' };

  const handleGoToProcess = (p) => {
    const key = PROCESS_CONFIRM_KEY[p.no];
    // 이미 확정되어 화면에서 사라진 이전 단계를 클릭하면 다시 열어서 수정할 수 있게 한다.
    if (key && stepConfirmed[key] && !isLocked) {
      handleUnconfirmStep(key);
      return;
    }
    const el = document.getElementById(p.id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---- Section 2: 고사실 ----
  const persistRoom = async (room) => {
    try {
      await upsert_testing_place_schedule({
        testing_place_schedule_id: room.testing_place_schedule_id,
        testing_place_id: testingPlace.testing_place_id,
        testing_schedule_id: testingSchedule.testing_schedule_id,
        code: room.code,
        room_no: room.room_no,
        max_users: room.max_users,
        main_teachers: room.main_teachers,
        sub_teachers: room.sub_teachers,
        data: room.data,
        status: room.status,
      });
    } catch (error) {
      console.error('고사실 저장 실패:', error);
      alert('고사실 정보를 저장하는데 실패했습니다.');
    }
  };

  const updateRoom = (index, patch) => {
    setRooms((prev) => {
      const next = prev.map((row, i) => (i === index ? { ...row, ...patch, status: TESTING_PLACE_SCHEDULE_STATUS.field.join } : row));
      persistRoom(next[index]);
      return next;
    });
  };

  const handleAddRoom = () => {
    const nextCode = rooms.length + 1;
    setRooms((prev) => [...prev, emptyRoom(testingPlace, testingSchedule, nextCode)]);
  };

  const handleRemoveRoom = (index) => {
    const room = rooms[index];
    if (!window.confirm('고사실을 삭제하시겠습니까?')) return;
    if (room.testing_place_schedule_id) {
      updateRoom(index, { status: TESTING_PLACE_SCHEDULE_STATUS.field.delete });
      setRooms((prev) => prev.filter((_, i) => i !== index));
    } else {
      setRooms((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const roomsMaxUsersSum = rooms.reduce((sum, r) => sum + (parseInt(r.max_users, 10) || 0), 0);
  const acceptUsers = liveAcceptUsers != null ? liveAcceptUsers : (parseInt(testingPlace?.accept_users, 10) || 0);
  const roomsMismatch = acceptUsers > 0 && roomsMaxUsersSum !== acceptUsers;

  // ---- Section 3: 진행위원 ----
  const handleTeacherCountChange = (key, value) => {
    const count = Math.max(0, parseInt(value, 10) || 0);
    setTeachers((prev) => {
      const prevDuties = Array.isArray(prev.duties?.[key]) ? prev.duties[key] : [];
      const nextDuties = Array.from({ length: count }, (_, i) => prevDuties[i] || '');
      return { ...prev, [key]: value, duties: { ...prev.duties, [key]: nextDuties } };
    });
  };
  const handleTeacherDutyItemChange = (key, index, value) => {
    setTeachers((prev) => {
      const list = Array.isArray(prev.duties?.[key]) ? [...prev.duties[key]] : [];
      list[index] = value;
      return { ...prev, duties: { ...prev.duties, [key]: list } };
    });
  };
  const teachersTotal = TEACHER_ROLES.reduce((sum, role) => sum + (parseInt(teachers[role.key], 10) || 0), 0);

  // ---- Section 4: 시설사용료 ----
  const handleAddCost = () => setCosts((prev) => [...prev, { item: '', calc: '', amount: 0, note: '' }]);
  const handleRemoveCost = (index) => setCosts((prev) => prev.filter((_, i) => i !== index));
  const handleCostChange = (index, field, value) => {
    setCosts((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };
  const costsTotal = costs.reduce((sum, c) => sum + (parseInt(c.amount, 10) || 0), 0);

  // ---- Section 2~4 순차 확정 게이트 (확정된 단계는 잠기고, 다음 단계만 열린다) ----
  const roomsDisabled = isLocked || stepConfirmed.rooms;
  const teachersDisabled = isLocked || !stepConfirmed.rooms || stepConfirmed.teachers;
  const costsDisabled = isLocked || !stepConfirmed.teachers || stepConfirmed.costs;
  const notesDisabled = isLocked || !stepConfirmed.costs || reviewMode;

  // 단계별로 한 섹션씩만 화면에 보여준다 — 확정하면 다음 섹션이 나타난다.
  // 이미 보고까지 끝난 경우(isLocked)에는 제출된 전체 내용을 한 번에 볼 수 있게 모두 표시한다.
  const currentSectionNo = isLocked
    ? null
    : !stepConfirmed.rooms
      ? 2
      : !stepConfirmed.teachers
        ? 3
        : !stepConfirmed.costs
          ? 4
          : 5;
  const showSection = (no) => currentSectionNo === null || currentSectionNo === no;

  const roomHeaders = [
    { name: 'NO.', value: '', renderCell: (item) => item.code },
    {
      name: '고사실 번호', value: '',
      renderCell: (item, index) => (
        <input type="text" value={item.room_no} disabled={roomsDisabled}
          onChange={(e) => updateRoom(index, { room_no: e.target.value })} />
      ),
      renderFooterCell: () => (
        <button onClick={handleAddRoom} disabled={roomsDisabled}>고사실 추가</button>
      ),
    },
    {
      name: '실제 장소명(선택)', value: '',
      renderCell: (item, index) => (
        <input type="text" value={item.data?.locationName || ''} disabled={roomsDisabled}
          placeholder="예: 1학년 1반"
          onChange={(e) => updateRoom(index, { data: { ...item.data, locationName: e.target.value } })} />
      ),
    },
    {
      name: '수용 인원', value: '',
      renderCell: (item, index) => (
        <input type="number" min="0" value={item.max_users} disabled={roomsDisabled}
          onChange={(e) => updateRoom(index, { max_users: e.target.value })} />
      ),
      renderFooterCell: () => (
        <span className={`sum-check ${roomsMismatch ? 'warn' : ''}`}>
          합계 {roomsMaxUsersSum}명
          {roomsMismatch && <b> (접수 {acceptUsers}명과 다릅니다)</b>}
        </span>
      ),
    },
    {
      name: '좌석 배치 형태', value: '',
      renderCell: (item, index) => (
        <select value={item.data?.seat?.direction || 'vertical'} disabled={roomsDisabled}
          onChange={(e) => updateRoom(index, { data: { ...item.data, seat: { ...item.data?.seat, direction: e.target.value } } })}>
          <option value="vertical">세로 배치</option>
          <option value="horizontal">가로 배치</option>
        </select>
      ),
    },
    {
      name: '비고', value: '',
      renderCell: (item, index) => (
        <input type="text" value={item.data?.note || ''} disabled={roomsDisabled}
          placeholder="예: 휠체어석 필요"
          onChange={(e) => updateRoom(index, { data: { ...item.data, note: e.target.value } })} />
      ),
    },
    {
      name: '', value: '',
      renderCell: (item, index) => (
        <button onClick={() => handleRemoveRoom(index)} disabled={roomsDisabled}>삭제</button>
      ),
    },
  ];

  // ---- Section 5: 저장/확정/제출 ----
  const buildPlanData = (extra = {}) => ({
    ...testingPlace.data,
    teachers,
    costs,
    notes,
    reportStatus,
    stepConfirmed,
    ...extra,
  });

  const handleSave = async (extra = {}) => {
    if (!testingPlace?.testing_place_id) {
      alert('고사장을 먼저 선택해 주세요.');
      return false;
    }
    const data = buildPlanData(extra);
    try {
      await upsert_testing_place({ testing_place_id: testingPlace.testing_place_id, data });
      setTestingPlace((prev) => ({ ...prev, data }));
      return true;
    } catch (error) {
      console.error('운용계획 저장 실패:', error);
      alert('저장 중 오류가 발생했습니다.');
      return false;
    }
  };

  const confirmStep = async (key) => {
    const nextStepConfirmed = { ...stepConfirmed, [key]: true };
    const ok = await handleSave({ stepConfirmed: nextStepConfirmed });
    if (ok) setStepConfirmed(nextStepConfirmed);
  };

  const handleConfirmRooms = () => {
    let msg = '고사실 세부 설정을 확정하시겠습니까?\n확정 후에는 다음 단계(진행위원 편성계획)로 진행되며, 수정이 필요하면 위 탭에서 다시 열 수 있습니다.';
    if (roomsMismatch) {
      msg = `고사실 수용인원 합계(${roomsMaxUsersSum}명)가 최종 확정 접수인원(${acceptUsers}명)과 다릅니다.\n${msg}`;
    }
    if (!window.confirm(msg)) return;
    confirmStep('rooms');
  };

  const handleConfirmTeachers = () => {
    if (!window.confirm('진행위원 편성계획을 확정하시겠습니까?\n확정 후에는 다음 단계(시설사용료 계획서)로 진행되며, 수정이 필요하면 위 탭에서 다시 열 수 있습니다.')) return;
    confirmStep('teachers');
  };

  const handleConfirmCosts = () => {
    if (!window.confirm('시설사용료 계획서를 확정하시겠습니까?\n확정 후에는 최종 확인 단계로 진행되며, 수정이 필요하면 위 탭에서 다시 열 수 있습니다.')) return;
    confirmStep('costs');
  };

  const handleUnconfirmStep = async (key) => {
    if (!window.confirm('이 항목을 다시 수정하시겠습니까?')) return;
    const nextStepConfirmed = { ...stepConfirmed, [key]: false };
    const ok = await handleSave({ stepConfirmed: nextStepConfirmed });
    if (ok) {
      setStepConfirmed(nextStepConfirmed);
      setReviewMode(false);
    }
  };

  const handleSubmitReport = async () => {
    if (roomsMismatch) {
      const ok = window.confirm(
        `고사실 수용인원 합계(${roomsMaxUsersSum}명)가 최종 확정 접수인원(${acceptUsers}명)과 다릅니다.\n그래도 보고하시겠습니까?`
      );
      if (!ok) return;
    }
    if (!window.confirm('운용 계획을 본부에 보고하시겠습니까? 보고 후에는 수정할 수 없습니다.')) return;

    const extra = { reportStatus: 'submitted', submittedAt: new Date().toISOString() };
    const ok = await handleSave(extra);
    if (ok) {
      setReportStatus('submitted');
      setReviewMode(false);
      alert('운용 계획이 본부에 보고되었습니다.');
    }
  };

  const hqName = testingHqs.find((hq) => hq.code === testingPlace?.gcode)?.name || '';

  // 수험표 출력시작일(confirmStartDate)부터 운용계획 보고를 진행할 수 있다.
  const now = new Date();
  const confirmStartDate = testingSchedule?.confirmStartDate ? new Date(testingSchedule.confirmStartDate) : null;
  const isBeforeConfirmStart = !(confirmStartDate && confirmStartDate <= now);

  return (
    <div id="testing_place_schedule_plan">
      <div className="head">
        <h1>고사장 운용계획 보고</h1>
      </div>
      <div className="body">
        <ul className="form">
          <li>
            <label>구분</label>
            <span>
              <select value={testingObj?.type || ''} onChange={handleTestingTypeChange}>
                <option value="">▒ 선택 ▒</option>
                {testingTypes.map((o, i) => <option key={i} value={o.code}>{o.nickname || o.name}</option>)}
              </select>
            </span>
          </li>
          <li>
            <label>시험</label>
            <span>
              <select value={testingObj?.testing_id || ''} onChange={handleTestingChange}>
                <option value="">▒ 선택 ▒</option>
                {testings.map((o, i) => <option key={i} value={o.testing_id}>{o.title}</option>)}
              </select>
            </span>
          </li>
          <li>
            <label>일정</label>
            <span>
              <select value={testingSchedule?.testing_schedule_id || ''} onChange={handleTestingScheduleChange}>
                <option value="">▒ 선택 ▒</option>
                {testingSchedules.map((o, i) => <option key={i} value={o.testing_schedule_id}>{o.testStartDate} ~ {o.testStopDate}</option>)}
              </select>
            </span>
          </li>
          <li>
            <label>고사장</label>
            <span>
              <select value={testingPlace?.testing_place_id || ''} onChange={handleTestingPlaceChange}>
                <option value="">▒ 선택 ▒</option>
                {testingPlaces.map((o, i) => <option key={i} value={o.testing_place_id}>{o.name}</option>)}
              </select>
            </span>
          </li>
        </ul>

        {!testingPlace || !testingSchedule ? (
          <div className="alert">구분 · 시험 · 일정 · 고사장을 모두 선택해 주세요.</div>
        ) : isBeforeConfirmStart ? (
          <div className="alert">
            수험표 출력시작일{confirmStartDate ? `(${confirmStartDate.toLocaleDateString('ko-KR')})` : ''}부터 운용계획을 작성할 수 있습니다.
          </div>
        ) : (
          <>
            <section className="plan-section plan-section-sticky" id="plan-section-1">
              <h2>기본 정보</h2>
              <table className="info-table">
                <tbody>
                  <tr>
                    <th>시험 회차</th>
                    <td>{testingObj?.title}</td>
                    <th>지역본부 / 고사장명</th>
                    <td>{hqName} / {testingPlace.name}</td>
                    <th>최종 확정 접수 인원</th>
                    <td>{acceptUsers}명</td>
                  </tr>
                  <tr>
                    <th>진행 상태</th>
                    <td colSpan={5}>
                      <ol className="status-flow">
                        {['draft', 'submitted', 'approved'].map((step, i) => {
                          const stepIndex = ['draft', 'submitted', 'approved'].indexOf(reportStatus);
                          const state = i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'todo';
                          return (
                            <li key={step} className={state}>
                              <span className="status-label">{REPORT_STATUS_LABEL[step]}</span>
                            </li>
                          );
                        })}
                      </ol>
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>

            <ol className="process-flow">
              {PROCESSES.map((p) => {
                const confirmKey = PROCESS_CONFIRM_KEY[p.no];
                const done = confirmKey ? !!stepConfirmed[confirmKey] : isLocked;
                const isCurrent = currentSectionNo === p.no;
                return (
                  <li
                    key={p.no}
                    className={isCurrent ? 'current' : done ? 'done' : 'todo'}
                    onClick={() => handleGoToProcess(p)}
                    title={`${p.label}로 이동`}
                  >
                    <span className="process-label">{p.label}</span>
                  </li>
                );
              })}
            </ol>

            {showSection(2) && (
            <section className="plan-section" id="plan-section-2">
              <div className="table-scroll">
                <ListBody headers={roomHeaders} items={rooms} className="room_table" showFooter />
              </div>
              {!isLocked && !stepConfirmed.rooms && (
                <div className="step-actions">
                  <button className="confirm-step" onClick={handleConfirmRooms}>고사실 설정 확정</button>
                </div>
              )}
            </section>
            )}

            {showSection(3) && (
            <section className="plan-section" id="plan-section-3">
              <div className="table-scroll">
                <table className="teacher-plan">
                  <thead>
                    <tr><th>구분</th><th>필요 인원</th><th>주요 업무 내용</th></tr>
                  </thead>
                  <tbody>
                    {TEACHER_ROLES.map((role) => (
                      <tr key={role.key}>
                        <td>{role.label}</td>
                        <td>
                          <span className="count-cell">
                            <input type="number" min="0" disabled={teachersDisabled}
                              value={teachers[role.key] ?? 0}
                              onChange={(e) => handleTeacherCountChange(role.key, e.target.value)} />
                            <span className="unit">명</span>
                          </span>
                        </td>
                        <td>
                          {(parseInt(teachers[role.key], 10) || 0) === 0 ? (
                            <span className="duty-empty">필요 인원을 입력하면 인원수만큼 업무 입력칸이 생깁니다.</span>
                          ) : (
                            <div className="duty-list">
                              {Array.from({ length: parseInt(teachers[role.key], 10) || 0 }, (_, i) => (
                                <div className="duty-item" key={i}>
                                  <span className="duty-no">{i + 1}:</span>
                                  <input type="text" disabled={teachersDisabled} placeholder={role.placeholder}
                                    value={teachers.duties?.[role.key]?.[i] || ''}
                                    onChange={(e) => handleTeacherDutyItemChange(role.key, i, e.target.value)} />
                                  <button type="button" className="duty-fill" disabled={teachersDisabled}
                                    onClick={() => handleTeacherDutyItemChange(role.key, i, role.placeholder)}>
                                    예시 입력
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr className="total"><td>총계</td><td>{teachersTotal}명</td><td /></tr>
                  </tbody>
                </table>
              </div>
              {!isLocked && stepConfirmed.rooms && !stepConfirmed.teachers && (
                <div className="step-actions">
                  <button className="cancel-step" onClick={() => handleUnconfirmStep('rooms')}>취소</button>
                  <button className="confirm-step" onClick={handleConfirmTeachers}>진행위원 편성계획 확정</button>
                </div>
              )}
            </section>
            )}

            {showSection(4) && (
            <section className="plan-section" id="plan-section-4">
              <div className="table-scroll">
                <table className="cost-plan">
                  <thead>
                    <tr><th>항목명</th><th>산출 내역(단가, 수량, 시간 등)</th><th>예정 금액(원)</th><th>비고</th><th /></tr>
                  </thead>
                  <tbody>
                    {costs.map((row, index) => (
                      <tr key={index}>
                        <td><input type="text" disabled={costsDisabled} value={row.item} placeholder="예: 시설 대관료"
                          onChange={(e) => handleCostChange(index, 'item', e.target.value)} /></td>
                        <td><input type="text" disabled={costsDisabled} value={row.calc} placeholder="예: 55,000원 x 10실"
                          onChange={(e) => handleCostChange(index, 'calc', e.target.value)} /></td>
                        <td><input type="number" min="0" disabled={costsDisabled} value={row.amount}
                          onChange={(e) => handleCostChange(index, 'amount', e.target.value)} /></td>
                        <td><input type="text" disabled={costsDisabled} value={row.note}
                          onChange={(e) => handleCostChange(index, 'note', e.target.value)} /></td>
                        <td><button onClick={() => handleRemoveCost(index)} disabled={costsDisabled}>삭제</button></td>
                      </tr>
                    ))}
                    <tr className="total"><td colSpan={2}>합계</td><td>{costsTotal.toLocaleString()}원</td><td colSpan={2} /></tr>
                  </tbody>
                </table>
              </div>
              <div className="section-foot">
                <button onClick={handleAddCost} disabled={costsDisabled}>항목 추가</button>
              </div>
              {!isLocked && stepConfirmed.teachers && !stepConfirmed.costs && (
                <div className="step-actions">
                  <button className="cancel-step" onClick={() => handleUnconfirmStep('teachers')}>취소</button>
                  <button className="confirm-step" onClick={handleConfirmCosts}>시설사용료 계획서 확정</button>
                </div>
              )}
            </section>
            )}

            {showSection(5) && (
            <section className="plan-section" id="plan-section-5">
              <ul className="form-vertical">
                <li>
                  <label>협회(시행관리팀) 전달 사항</label>
                  <textarea disabled={notesDisabled} value={notes}
                    placeholder="예: 정문 공사로 인해 후문 개방 안내 요망, 주차장 통제 등 특이사항 기재"
                    onChange={(e) => setNotes(e.target.value)} />
                </li>
                <li>
                  <label>진행 상태</label>
                  <span className={`status-badge ${reportStatus}`}>{REPORT_STATUS_LABEL[reportStatus]}</span>
                </li>
              </ul>

              {!isLocked && stepConfirmed.costs && !reviewMode && (
                <div className="step-actions">
                  <button className="cancel-step" onClick={() => handleUnconfirmStep('costs')}>취소</button>
                  <button onClick={() => handleSave()}>임시 저장</button>
                  <button className="submit" onClick={() => setReviewMode(true)}>제출 전 내용 확인하기</button>
                </div>
              )}

              {!isLocked && reviewMode && (
                <div className="review-summary">
                  <h3>제출 전 최종 확인</h3>

                  <div className="review-block">
                    <h4>2. 고사실 세부 설정</h4>
                    <table className="review-table">
                      <thead>
                        <tr><th>NO.</th><th>고사실 번호</th><th>실제 장소명</th><th>수용 인원</th><th>좌석 배치</th><th>비고</th></tr>
                      </thead>
                      <tbody>
                        {rooms.map((r) => (
                          <tr key={r.code}>
                            <td>{r.code}</td>
                            <td>{r.room_no}</td>
                            <td>{r.data?.locationName || '-'}</td>
                            <td>{r.max_users}명</td>
                            <td>{r.data?.seat?.direction === 'horizontal' ? '가로 배치' : '세로 배치'}</td>
                            <td>{r.data?.note || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="review-sum">수용인원 합계: {roomsMaxUsersSum}명</p>
                  </div>

                  <div className="review-block">
                    <h4>3. 진행위원 운영 계획</h4>
                    <table className="review-table">
                      <thead><tr><th>구분</th><th>인원</th><th>업무 내용</th></tr></thead>
                      <tbody>
                        {TEACHER_ROLES.map((role) => (
                          <tr key={role.key}>
                            <td>{role.label}</td>
                            <td>{parseInt(teachers[role.key], 10) || 0}명</td>
                            <td className="review-duties">
                              {(teachers.duties?.[role.key] || []).some((d) => d)
                                ? teachers.duties[role.key].map((d, i) => d ? <div key={i}>{i + 1}. {d}</div> : null)
                                : '-'}
                            </td>
                          </tr>
                        ))}
                        <tr className="total"><td>총계</td><td>{teachersTotal}명</td><td /></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="review-block">
                    <h4>4. 시설 사용료 내역</h4>
                    {costs.length === 0 ? (
                      <p>입력된 항목이 없습니다.</p>
                    ) : (
                      <table className="review-table">
                        <thead><tr><th>항목명</th><th>산출 내역</th><th>예정 금액</th><th>비고</th></tr></thead>
                        <tbody>
                          {costs.map((c, i) => (
                            <tr key={i}>
                              <td>{c.item || '-'}</td>
                              <td>{c.calc || '-'}</td>
                              <td>{(parseInt(c.amount, 10) || 0).toLocaleString()}원</td>
                              <td>{c.note || '-'}</td>
                            </tr>
                          ))}
                          <tr className="total"><td colSpan={2}>합계</td><td>{costsTotal.toLocaleString()}원</td><td /></tr>
                        </tbody>
                      </table>
                    )}
                  </div>

                  <div className="review-block">
                    <h4>5. 특이사항</h4>
                    <p>{notes || '없음'}</p>
                  </div>

                  <div className="step-actions">
                    <button className="cancel-step" onClick={() => setReviewMode(false)}>수정하기</button>
                    <button className="submit" onClick={handleSubmitReport}>운용 계획 본부 보고하기</button>
                  </div>
                </div>
              )}
            </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TestingPlaceSchedulePlan;
